import { AuditorProfileNoticeDialogService } from '../../components/auditor-profile-notice-dialog/shared/services/auditor-profile-notice-dialog.service';
import { ERoleName } from '../models/enums/role.enum';
import { CurrentAssessorProfileService } from '../services/assessor/current-assessor-profile.service';
import { StateService } from '../services/state/state.service';
import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { StatusCodes } from 'http-status-codes';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/**
 * Restricts assessor detail routes so the Auditor role can open only their own profile.
 */
@Injectable({
  providedIn: 'root',
})
export class AuditorSelfProfileAccessGuard implements CanActivate, CanActivateChild {
  private readonly _stateService = inject(StateService);
  private readonly _currentAssessorProfileService = inject(CurrentAssessorProfileService);
  private readonly _auditorProfileNoticeDialog = inject(AuditorProfileNoticeDialogService);
  private readonly _router = inject(Router);

  /**
   * Allows navigation or blocks it when the auditor is not viewing their own profile.
   * @param {ActivatedRouteSnapshot} route - Activated route snapshot.
   * @param {RouterStateSnapshot} _state - Target router state.
   * @returns {Observable<boolean | UrlTree>} Access result.
   */
  public canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this._canAccess$(route);
  }

  /**
   * Allows child navigation or blocks it when the auditor is not viewing their own profile.
   * @param {ActivatedRouteSnapshot} childRoute - Activated child route snapshot.
   * @param {RouterStateSnapshot} _state - Target router state.
   * @returns {Observable<boolean | UrlTree>} Access result.
   */
  public canActivateChild(childRoute: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this._canAccess$(childRoute);
  }

  /**
   * Resolves whether the current user may open the requested assessor detail route.
   * @param {ActivatedRouteSnapshot} route - Route snapshot containing `auditorId`.
   * @returns {Observable<boolean | UrlTree>} Access result.
   */
  private _canAccess$(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    if (this._stateService.selectedRole() !== ERoleName.AUDITOR) {
      return of(true);
    }

    const routeAuditorId = (this._getAuditorIdFromRoute(route) ?? '').trim();

    if (routeAuditorId === '') {
      return of(this._router.parseUrl('/home'));
    }

    return this._currentAssessorProfileService.profileId$().pipe(
      map((profileId): boolean | UrlTree => {
        const ownProfileId = (profileId ?? '').trim();

        if (ownProfileId === '') {
          this._auditorProfileNoticeDialog.openNotCreated();

          return this._router.parseUrl('/home');
        }

        if (ownProfileId === routeAuditorId) {
          return true;
        }

        this._auditorProfileNoticeDialog.openAccessDenied();

        return false;
      }),
      catchError(
        (): Observable<UrlTree> =>
          of(
            this._router.createUrlTree(['/error'], {
              queryParams: { code: StatusCodes.INTERNAL_SERVER_ERROR },
            })
          )
      )
    );
  }

  /**
   * Reads `auditorId` from the current route or an ancestor.
   * @param {ActivatedRouteSnapshot} route - Route snapshot to inspect.
   * @returns {string | null} Assessor profile id from the URL, if present.
   */
  private _getAuditorIdFromRoute(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;

    while (current !== null) {
      const auditorId = current.paramMap.get('auditorId');

      if (auditorId !== null) {
        return auditorId;
      }

      current = current.parent;
    }

    return null;
  }
}
